import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { skillQueryKeys } from "./skillQueryKeys";

/**
 * Fetch one skill's summary — the record, its latest version, and the caller's
 * subscription row.
 *
 * The skill detail page's loader. Deliberately not a lookup into `useSkills`'s
 * cache: the detail page is reachable directly from a share link, where paging
 * the org's whole skill list (up to 1000 bodies) to find one row would be the
 * slow way to answer a question the server can answer with a single row. It
 * also sidesteps the scope question entirely — a summary is scope-free, where
 * the listing is split into Personal and Org by the reader's own relationship
 * to each skill.
 *
 * Rejects with a 404 when the skill doesn't exist *or* isn't visible to the
 * caller; the server does not distinguish the two.
 */
export function useSkillSummary(
  orgId: string | undefined,
  skillId: string | undefined,
) {
  const { skillService } = useDomainServices();

  return useQuery({
    queryKey: skillQueryKeys.detail(orgId, skillId),
    queryFn: ({ signal }) =>
      skillService.getSkillSummary(skillId!, { abortSignal: signal, orgId }),
    enabled: orgId !== undefined && skillId !== undefined,
  });
}
